/**
 * =========================================================================================
 * UForceLogout — pure helpers (no React, no socket)
 * =========================================================================================
 **/

import { getCookie } from '../common/function';
import { isInApp } from '../common/function';

/**
 * Decode a JWT payload without verifying the signature.
 * Backend already verified — we only need the claims for the sessionId filter.
 * Returns null on any malformed input so callers degrade safely.
 */
export const decodeJwt = (token) => {
    if (!token || typeof token !== 'string') return null;
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(decodeURIComponent(escape(atob(padded))));
    } catch (e) {
        return null;
    }
};

/**
 * Pull the current tab's sessionId from the ubs_token cookie.
 * Pre-spec sessions may not have the claim — returns null in that case.
 */
export const getMySessionId = () => {
    const claims = decodeJwt(getCookie('ubs_token'));
    return claims && claims.sessionId ? claims.sessionId : null;
};

/**
 * Force-logout broadcast filter.
 *
 *  - Must be a `forceLogout: true` packet (cheap check first).
 *  - If the backend includes `data.session.sessionId` AND this tab has a
 *    sessionId claim in its JWT, the two must match — this is the
 *    multi-tab-same-user disambiguator for room-broadcast delivery.
 *  - If EITHER side omits the sessionId (backend doesn't supply it OR the
 *    tab's JWT predates the claim), trust the broadcast. Backend has
 *    already chosen which socket to target — and broadcasts for
 *    "Sign out one device" / "Sign out all other devices" arrive at the
 *    receiving socket without a session block.
 *
 * This keeps the strict match when both sides supply it (correct under
 * room-broadcast) and accepts targeted emits without the field (correct
 * under direct socket emit). Both are real backend paths.
 */
export const shouldForceLogout = (res, mySessionId) => {
    if (!res || !res.data || res.data.forceLogout !== true) return false;
    const incoming = res.data.session && res.data.session.sessionId;
    if (incoming && mySessionId) return incoming === mySessionId;
    return true;
};

/**
 * Build the post-Done redirect URL.
 *  - account / admin authenticated UI → same-origin /login
 *  - product apps → ${REACT_APP_ACCOUNT_MODULE_URL}/login   (no orgId in URL)
 *  - in-app webview → ${REACT_APP_ACCOUNT_MODULE_URL}/?platform=app
 *  - missing env var → fall back to /login (safe; user lands somewhere)
 *
 * `orgId` is intentionally NOT appended to cross-app redirects — the Account
 * module's /login route resolves the correct org from the user's session.
 */
export const resolveLoginUrl = (options) => {
    const opts = options || {};
    if (opts.isAccountApp) return '/login';

    const base = process.env.REACT_APP_ACCOUNT_MODULE_URL || '';

    try {
        if (typeof isInApp === 'function' && isInApp()) {
            return base ? `${base}/?platform=app` : '/login';
        }
    } catch (e) {
        // isInApp() failures must not block redirect
    }

    if (base) return `${base}/login`;
    return '/login';
};
